/**
 * PropertyFeatures Component
 * 
 * Core component for displaying property features and specifications
 */

'use client';

import type { Property } from '@/lib/api/clients/properties';
import { 
  Bed, 
  Bathtub, 
  Square, 
  Calendar, 
  Car, 
  Mountains, 
  Buildings,
  ArrowsOutSimple 
} from '@phosphor-icons/react/dist/ssr';

export interface PropertyFeaturesProps {
  property: Property;
  messages: {
    keyFeatures: {
      title: string;
      bedrooms: string;
      bathrooms: string;
      area: string;
      landArea: string;
      yearBuilt: string;
      yearRenovated: string;
      parking: string;
      storeys: string;
      floorNumber: string;
    };
  };
  className?: string;
}

export function PropertyFeatures({
  property,
  messages,
  className = ''
}: PropertyFeaturesProps) {
  const features = [
    {
      icon: <Bed size={24} weight="regular" />,
      label: messages.keyFeatures.bedrooms,
      value: property.bedrooms,
      show: !!property.bedrooms,
    },
    {
      icon: <Bathtub size={24} weight="regular" />,
      label: messages.keyFeatures.bathrooms,
      value: property.bathrooms,
      show: !!property.bathrooms,
    },
    {
      icon: <Square size={24} weight="regular" />,
      label: messages.keyFeatures.area,
      value: property.totalFloorArea 
        ? `${property.totalFloorArea} ${property.totalFloorAreaUnit?.toLowerCase() || 'sqm'}` 
        : null,
      show: !!property.totalFloorArea,
    },
    {
      icon: <ArrowsOutSimple size={24} weight="regular" />,
      label: messages.keyFeatures.landArea,
      value: property.landArea 
        ? `${property.landArea} ${property.landAreaUnit?.toLowerCase() || 'sqm'}` 
        : null,
      show: !!property.landArea,
    },
    {
      icon: <Calendar size={24} weight="regular" />,
      label: messages.keyFeatures.yearBuilt,
      value: property.yearBuilt,
      show: !!property.yearBuilt,
    },
    {
      icon: <Calendar size={24} weight="regular" />,
      label: messages.keyFeatures.yearRenovated,
      value: property.yearRenovated,
      show: !!property.yearRenovated,
    },
    {
      icon: <Car size={24} weight="regular" />,
      label: messages.keyFeatures.parking,
      value: property.parkingSpaces || property.garageCount,
      show: !!(property.parkingSpaces || property.garageCount),
    },
    {
      icon: <Buildings size={24} weight="regular" />,
      label: messages.keyFeatures.storeys,
      value: property.storeys,
      show: !!property.storeys,
    },
    {
      icon: <Mountains size={24} weight="regular" />,
      label: messages.keyFeatures.floorNumber,
      value: property.floorNumber,
      show: !!property.floorNumber,
    }
  ].filter(f => f.show);

  if (features.length === 0) {
    return null;
  }

  return (
    <div className={`font-inter ${className}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-8 bg-gradient-to-b from-[#BBA339] to-[#E4CE4A] rounded-full"></div>
        <h2 className="text-xl md:text-2xl font-bold text-[#222222] font-inter">
          {messages.keyFeatures.title}
        </h2>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {features.map((feature, index) => (
          <div key={index} className="group flex flex-col items-start gap-2 p-4 rounded-xl hover:bg-gradient-to-br hover:from-[#BBA339]/5 hover:to-transparent transition-all duration-200 border border-transparent hover:border-[#BBA339]/20">
            <div 
              className="w-12 h-12 flex items-center justify-center mb-1 rounded-xl bg-gradient-to-br from-[#243462]/10 to-[#243462]/5 text-[#243462] group-hover:from-[#BBA339]/20 group-hover:to-[#E4CE4A]/10 group-hover:text-[#BBA339] transition-all"
            >
              {feature.icon}
            </div>
            <div className="text-xl font-bold text-[#222222] group-hover:text-[#243462] transition-colors">
              {feature.value}
            </div>
            <div className="text-sm text-[#717171] font-medium">
              {feature.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
